import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import type { PopupData } from 'webapp-types';
import { GoalCalculationStrategy } from 'webapp/enums/enums';

import type { BoxItemInfo } from 'webapp/components/form/radiobutton-box-group';
import FormRadiobuttonBoxGroupComponent from 'webapp/components/form/radiobutton-box-group';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import SubmitButtonComponent from 'webapp/components/submit-button';

export const CALCULATION_STRATEGY_LABELS: Record<GoalCalculationStrategy, string> = {
	[GoalCalculationStrategy.LAST_POINT]: 'Last value',
	[GoalCalculationStrategy.AVERAGE]: 'Average',
	[GoalCalculationStrategy.SUM]: 'Sum',
};

const CALCULATION_STRATEGY_DESCRIPTIONS: Record<GoalCalculationStrategy, string> = {
	[GoalCalculationStrategy.LAST_POINT]:
		'Uses the value from the last day of the period. Best for balances and running totals.',
	[GoalCalculationStrategy.AVERAGE]:
		'Averages daily values across the period. Best for rates, ratios and percentages.',
	[GoalCalculationStrategy.SUM]:
		'Adds up daily values across the period. Best for counts like sessions or revenue.',
};

const SUM_NOT_ALLOWED_TOOLTIP =
	'Sum is not available for this metric because its values are not reset each period.';

const STRATEGIES_ORDER = [
	GoalCalculationStrategy.LAST_POINT,
	GoalCalculationStrategy.AVERAGE,
	GoalCalculationStrategy.SUM,
];

export default class GoalsCalculationStrategyPopupComponent extends PopupInnerContextComponent<PopupData.Goal.CalculationStrategy> {
	@tracked pendingStrategy: GoalCalculationStrategy =
		this.popupData.selected ?? this.popupData.autoStrategy;

	get isPendingDefault() {
		return this.pendingStrategy === this.popupData.autoStrategy;
	}

	get options(): BoxItemInfo[] {
		const { autoStrategy, isSumAllowed } = this.popupData;

		return STRATEGIES_ORDER.map((strategy) => {
			const isDisabled = strategy === GoalCalculationStrategy.SUM && !isSumAllowed;
			return {
				value: strategy,
				label: CALCULATION_STRATEGY_LABELS[strategy],
				label2: CALCULATION_STRATEGY_DESCRIPTIONS[strategy],
				badge: strategy === autoStrategy ? 'Default' : undefined,
				isDisabled,
				tooltip: isDisabled ? SUM_NOT_ALLOWED_TOOLTIP : undefined,
			};
		});
	}

	@action
	changeStrategy(value: AnySimpleValue) {
		this.pendingStrategy = value as GoalCalculationStrategy;
	}

	@action
	apply() {
		this.popupData.onApply(this.isPendingDefault ? null : this.pendingStrategy);
		this.close();
	}

	<template>
		<div class="calculation-strategy">
			<div class="calculation-strategy__title">Calculation strategy</div>
			<div class="calculation-strategy__description">
				How daily values roll up into weekly, monthly, quarterly and yearly targets.
			</div>
			<FormRadiobuttonBoxGroupComponent
				@name="calculation-strategy"
				@selected={{this.pendingStrategy}}
				@options={{this.options}}
				@onChange={{this.changeStrategy}}
			/>
			<hr />
			<div class="calculation-strategy__controls">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent @text="Apply" @noDelay={{true}} @onTrigger={{this.apply}} />
			</div>
		</div>
	</template>
}
